import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { message } from 'ant-design-vue';
import { UPLOAD_MAX_MB } from 'shared';

/**
 * 全局 HTTP 客户端（axios 封装）
 *
 * - baseURL：开发与生产均为 `/api`（dev 由 vite proxy 转发到 :3001）
 * - 请求拦截：自动从 auth store 注入 Bearer Token
 * - 响应拦截：解包 `{ code: 0, message, data }`；401 跳转登录；其他错误用 message.error 提示
 *
 * 注：使用动态 import 引入 store 以避免与 stores/auth.ts 形成循环依赖；
 * 拦截器在请求/响应时才会执行，那时 pinia 已通过 main.ts 完成初始化。
 */
const http: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 120_000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();
  if (auth.token) {
    config.headers.set?.('Authorization', `Bearer ${auth.token}`);
  }
  return config;
});

http.interceptors.response.use(
  (response: AxiosResponse) => {
    const body = response.data;
    // 标准响应包：{ code, message, data }
    if (body && typeof body === 'object' && 'code' in body) {
      if (body.code === 0) return body.data;
      // 业务错误码：提示后抛出，调用方可继续 catch 处理
      message.error(body.message || '请求失败');
      return Promise.reject(body);
    }
    // 非标准响应直接返回原始 body
    return body;
  },
  async (error) => {
    const status = error.response?.status;
    let msg = error.response?.data?.message || error.message || '请求失败';
    if (status === 413) {
      msg = `文件过大，单个文件最大 ${UPLOAD_MAX_MB}MB`;
    }
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const payload = JSON.parse(text) as { message?: string; code?: string; data?: unknown };
        msg = payload.message || msg;
      } catch {
        // 非 JSON 文件响应保留原错误信息。
      }
    }
    if (status === 401) {
      const { useAuthStore } = await import('@/stores/auth');
      const auth = useAuthStore();
      // 区分两类 401：
      //  - 已有会话（持有 token）→ 视为登录态过期，静默注销并跳登录页；
      //  - 无会话（如登录请求本身凭证错误）→ 没有可注销的会话，应弹出失败提示，
      //    避免错误账号登录时「无任何反馈」（满足登录失败需展示提示）。
      if (auth.token) {
        auth.logout();
      } else {
        message.error(msg);
      }
    } else {
      message.error(msg);
    }
    return Promise.reject(error);
  },
);

export default http;
